import { PoolsAPIFetchPoolResponse } from 'api/pools';
import * as PoolsAPI from 'api/pools';
import { DATA_BAG } from 'defs/offlineAvailability';
import Sequences from 'defs/sequences';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import * as assetsSagas from 'sagas/assets';
import { loadFavoritesData } from 'sagas/favorites';
import { actionCreators as assetsAC } from 'store/assets';
import { actionCreators as connectionAC, getIsOnline } from 'store/connection';
import { getFavoriteIds } from 'store/favorites';
import { actionTypes as loginAT, getHasAuthenticationLoaded, getIsAuthenticated } from 'store/login';
import { actionCreators as offlineAvailabilityAC } from 'store/offlineAvailability';
import { actionCreators as poolsAC } from 'store/pools';
import { actionCreators as sequencesAC } from 'store/sequences';
import { actionCreators as transientPoolAC, getProgramItemExternalRef } from 'store/transientPoolState';

export function* getPool(action: ReturnType<typeof poolsAC.doRequestPool>): Iterable<any> {
	const isOnline = yield select(getIsOnline);

	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	const isAuthenticated = yield select(getIsAuthenticated);
	const hasAuthenticationLoaded = yield select(getHasAuthenticationLoaded);

	if (!isAuthenticated && !hasAuthenticationLoaded) {
		yield take(loginAT.LOGIN_STATUS_KNOWN);
	}

	const favoriteIds = ((yield select(getFavoriteIds)) as any) as string[];
	if (!favoriteIds || favoriteIds.length === 0) {
		yield fork(loadFavoritesData);
	} else {
		loadFavoritesData();
	}

	const requiredAssets = [assetsAC.doLoadAsset('workout/fallback')];
	yield all(
		requiredAssets.map((requiredAssetsAction: ReturnType<typeof assetsAC.doLoadAsset>) =>
			fork(assetsSagas.loadAsset, requiredAssetsAction)
		)
	);

	try {
		yield put(sequencesAC.doStartSequence(Sequences.POOL_IS_LOADING));
		const programItemExternalRef: any = yield select(getProgramItemExternalRef);

		const response: PoolsAPIFetchPoolResponse = (yield call(
			(PoolsAPI as any).fetchPool,
			action.selectedPool,
			programItemExternalRef
		)) as any;

		if (response) {
			yield put(poolsAC.doRequestPoolSuccess(action.selectedPool, response.items));
			yield put(sequencesAC.doFinishSequence(Sequences.POOL_IS_LOADING));
			yield put(
				offlineAvailabilityAC.doRegisterDataBag(DATA_BAG.POOL, {
					id: action.selectedPool,
				})
			);
		}
	} catch (err) {
		yield put(poolsAC.doRequestPoolFailure(action.selectedPool, err as any));
	}
}

export function* loadSelectedPoolItem(action: ReturnType<typeof transientPoolAC.doLoadSelectedItem>): Iterable<any> {
	const isOnline = yield select(getIsOnline);

	if (!isOnline) {
		return yield put(connectionAC.doTrackNetworkFetchRequestError());
	}

	const isAuthenticated = yield select(getIsAuthenticated);
	const hasAuthenticationLoaded = yield select(getHasAuthenticationLoaded);

	if (!isAuthenticated && !hasAuthenticationLoaded) {
		yield take(loginAT.LOGIN_STATUS_KNOWN);
	}

	const favoriteIds = ((yield select(getFavoriteIds)) as any) as string[];
	if (!favoriteIds || favoriteIds.length === 0) {
		yield fork(loadFavoritesData);
	}

	try {
		const query = `${action.contentType.toUpperCase()}:${action.id}`;
		const item = yield call((PoolsAPI as any).fetchPoolItem, query);

		if (item) {
			yield put(transientPoolAC.doLoadSelectedItemSuccess((item[0] || {}) as any, action.id));
		} else {
			throw new Error(`No response for ${query}`);
		}
	} catch (err) {
		yield put(transientPoolAC.doLoadSelectedItemFailure(err as any));
	}
}
